import { DependencyOperatorTypes } from 'utils/compares';
import { AddressTypes } from 'utils/enums/address';
import { DistanceTypes, DriverAssignmentStrategy } from 'utils/enums/common';
import { GeoSystemTypes } from 'utils/enums/geo-system-types';
import { MapTileStyles } from 'utils/enums/map-tile-styles';
import { MapServerTypes } from 'utils/enums/osrm';
import { AddressLocation } from 'views/MAddress/MAddress.model';
import { MSelectOption } from 'views/MSelect/MSelect.model';
import { MServerSelectHttpServices } from 'views/MServerSelect/MServerSelect.model';

import { FixedSizeArray } from './http/general';
import { TripAssignmentTypes, TripsJoinModes } from './http/trip';

export enum SettingsEntityTypes {
  Company = 0,
  Site = 1,
  Profile = 2,
  Templates = 3,
}

export enum WaitAndReturnGroupingModes {
  Off = 'off',
  IndividualPair = 'individual-pair',
  MultiplePairs = 'multiple-pairs',
}

export enum RoutingMode {
  Default = 'Default',
  HClustering = 'HClustering',
  VRP = 'VRP',
}

export enum SupplierPriorityModes {
  ByRank = 'byRank',
  ByTier = 'byTier',
}

export type IEngineTrafficHours = FixedSizeArray<24, number>;

export type ICoordinates = [number, number];

export enum SettingsCategories {
  Geolocation = 'Geolocation',
  EngineRouteSettings = 'EngineRouteSettings',
  EngineTrafficSettings = 'EngineTrafficSettings',
  PaymentSettings = 'PaymentSettings',
  EngineAssignSettings = 'EngineAssignSettings',
  DriverScheduleSettings = 'DriverScheduleSettings',
  Bookings = 'Bookings',
  Auth = 'Auth',
  Distribution = 'Distribution',
}

export enum WeekendTypes {
  Sunday = 'sunday',
  Saturday = 'saturday',
}

export enum ReservationsClusterSnapTo {
  Address = 'address',
  BusStop = 'bus',
}

export enum PaymentProviders {
  Stripe = 'stripe',
  Cardknox = 'cardknox',
}

export enum FieldsTypes {
  Number = 'number',
  Boolean = 'boolean',
  String = 'string',
  Array = 'array',
  Checkbox = 'checkbox',
  Slider = 'slider',
  Select = 'select',
  SearchAddress = 'searchAddress',
}

export type ISettingsFields = {
  [SettingsCategories.Geolocation]: {
    /** Define geographical system type */
    systemType: GeoSystemTypes;

    /** Define addrsses type  */
    autocompleteType: AddressTypes;

    /** Enable per-location timezone resolution */
    enableLocationTimezone: boolean;

    /** Every entity has a map center lat, lon that effects what the client will see on all of our map components */
    mapCenter: AddressLocation;

    /** How far/close the client will see the maps in the platform */
    mapZoom: number;

    /** Server URL for map display */
    mapServer: MapServerTypes;

    /** Tile imagery style used on all maps across the platform */
    mapStyle: MapTileStyles;

    /** Whether GraphHopper routing engine is enabled */
    graphhopperEnabled: boolean;

    /** Timezone  */
    timezone: string;

    dateFormat: string;

    defaultLanguage: string;
  };
  [SettingsCategories.Auth]: {
    is2FAEnforced: boolean;
  };
  [SettingsCategories.Bookings]: {
    preventDuplications: boolean;
    isWaitAndReturnEnabled: boolean;
  };
  [SettingsCategories.EngineRouteSettings]: {
    /**limit the number of minutes a driver can drive between the destination of his last trip (his current location) and the origin of the considered next trip. */
    maxDurNext: number;

    /**
     * Whether reservations clustering algotithms are enabled or not.
     * @engine clusterReservationsRadius (as-is when enabled or as -1 when disabled)
     */
    isClusteringEnabled: boolean;

    /**
     * In what diameter to start combine reservation together into one pickup/drop spot (in meters/feets according to the defined geo unit system)
     * @engine clusterRadius
     */
    clusterReservationsRadius: number;
    // TODO: rename Radius to Diameter for the following in backend/frontend (+migration):
    // clusterReservationsRadius -> clusterReservationsDiameter
    // clusterRadius -> clusterDiameter

    /**
     * Defines weither or not a cluster center address should be changed with a known road or a bus stop
     * @engine clusterSnapAddress & snapToBusStop
     */
    clusterSnapTo: ReservationsClusterSnapTo;

    /**
     * Maximum distance (in meters/feets according to the defined geo unit system) in which cluster center address could be modified for a better location
     * @engine snapMaxDis
     */
    clusterSnapMaxDistance: number;

    /**
     * The system will attempt to create additional stops along the route if the passenger's air distance is shorter than the selected ratio
     * @engine snapToShapeRatio
     */
    snapToShapeRatio: number;

    /**
     * Minimum distance between created stops when the setting "Priority Stops/Clusters by Air Distance" is active
     *  @engine snapToShapeUnite
     */
    snapToShapeUnite: number;

    /**
     * If the ratio between shared-ride and driving alone excceded X for a passenger they will be driving alone.
     * @engine ratio
     */
    sharedRideRatio: number;

    /**
     * If the time difference between shared-ride and driving alone excceded X minutes, a passenger will be driving alone.
     * @engine addition
     */
    sharedRideDifferenceLimit: number;

    /**
     * Defines how much longer a shared ride is allowed to be compared to the solo distance. Example: If solo distance is 1 mile and the ratio is 1.2x, the shared ride for that passenger cannot exceed 1.2 miles
     * @engine
     */
    maxSharedDistanceRatio: number;

    /**
     * When reaching a stop, the driver will wait until the exact pick-up time, if needed.
     * @engine waitRequest
     */
    waitForBoardingTime: boolean;

    /**
     * Maximum stops locations per trip.
     * @engine maxStops
     */
    maxTripStops: number;

    /**
     * Maximum duration of a trip in minutes.
     * @engine maxDuration
     */
    maxTripDurationWithRanges: {
      maxTripDuration: number;
      ranges: {
        from: number;
        to: number;
        duration: number;
      }[];
    };

    /**
     * Max Single Passenger Trip Time.
     * @engine sla
     */
    maxSinglePassengerTimeWithRanges: {
      maxSinglePassengerTime: number;
      ranges: {
        from: number;
        to: number;
        duration: number;
      }[];
    };

    /**
     * Maximum number of passengers in the entire trip.
     * @engine pax
     */
    maxPassengers: number;

    /**
     * Sets the option to allow users to manually reorder stops within a trip by dragging them on the map.
     */
    editOnMap: boolean;

    /**
     * Enables consistent passenger routing as a system-level default.
     */
    consistentPassengerRouting: boolean;

    /**
     * Enables same-driver-for-group as a system-level default.
     */
    sameDriverForGroup: boolean;

    /**
     * Maximum number of passengers allowed to be occupied at the same time (a seat will be free/used on passengers drop/pickup)
     * @engine paxAtOnce
     */
    maxPassengersAtOnce: number;

    /**
     * Maximum number of minutes a trip is allowed to be empty between stops
     * @engine emptyLegDur
     */
    maxEmptyTripTime: number;

    /**
     * Maximum number of reservations allowed to be in the vehicle at the same time.
     * @engine jobsAtOnce
     */
    maxReservationsAtOnce: number;

    /**
     * Maximum number of iterations the engine will make until it decide on the most optimizied trips (the bigger the slower)
     * @engine maxFails
     */
    maxRouteIterations: number;

    /**
     * Reservations will be merged together in some conditions
     * @engine sameFrom / sameTo / sameAreaFrom / sameAreaTo
     */
    mergeReservationsByBoardingArea: boolean;
    mergeReservationsByBoardingLocation: boolean;
    mergeReservationsByDropArea: boolean;
    mergeReservationsByDropLocation: boolean;

    /**
     * Bookings will be grouped together only if the pickup or drop-off locations are the same.
     * @engine sameFromOrTo
     */
    onlyGroupExactLocations: boolean;

    /**
     * The time it takes to pickup a passenger on their stop (This parameter will impact overall trip duration)
     * @engine pickSeconds
     */
    tripBoardingTime: number;

    /**
     * When the setting is off, boarding times follow the general 'Boarding Time' setting. Activating this setting customizes boarding times specifically for non-first passengers
     * @engine pickSecondsOthers
     */
    subsequentPickTime: number;

    /**
     * The time it takes to drop a passenger on their stop (This parameter will impact overall trip duration)
     * @engine dropSeconds
     */
    tripDropOffTime: number;

    /**
     * When the setting is off, boarding times follow the general 'Drop-off Time' setting. Activating this setting customizes drop-off times specifically for non-first passengers
     * @engine dropSecondsOthers
     */
    subsequentDropOffTime: number;

    /**
     * Minimum distance (in meters) a reservation should have between pickup and drop-off location in order to be included in the route.
     * Reservation with a smaller distance than that would be completly ignored by the engine.
     * @engine minJobDis
     */
    minReservationDistance: number;

    /**
     * Decide if the engine will try to join new reservations to given trips
     * @engine tryJoin
     */
    tripsJoinMode: TripsJoinModes;

    /**
     * When set to true, all stops in the trip will be ordered by the pick-up time only, and not geographically.
     * @engine byPick
     */
    sortStopsByPickupTime: boolean;

    /**
     * When set to true, the trip will first pick up all passengers and then start with dropping everyone off.
     * @engine pickAll
     */
    pickupAllBeforeDropping: boolean;

    /**
     * Avoids routing of legs that are Angle Duration minutes or longer and are in an angle more than Angle.
     * @engine angleDur
     */
    angleDuration: number;

    /**
     * Avoids routing of legs that are Angle Duration minutes or longer and are in an angle more than Angle
     */
    angleMax: number;

    /** If enabled, allow reservations to be added to an in-route trips. */
    allowActiveTripsRouteChanges: boolean;

    /**
     * If enabled, editing a booking that belongs to a trip reroutes the trip and keeps the booking on it while the
     * trip and its driver are still valid. If disabled, the edited booking is removed from its trip.
     */
    rerouteTripOnBookingUpdate: boolean;

    /**
     * The API can save calculation times, if theoretically, the driver does not have a chance to reach the current trip start.
     * it's recommended to leave this parameter at "200". This means that even if there was a straight road and the driver speeded 200 KMPH,
     * and still could not reach the target in time, the engine will not consider this driver, and no extra calculations are needed.
     */
    airKMPH: number;

    /**
     * Adjusts the departure time so that passenger's waiting times will be balanced.
     * @engine normalizeDeparture
     */
    balanceDepartureTime: boolean;

    /**
     * Maximum number of minutes a driver can be late for the pickup time set in the reservation
     * @engine pickGapAfter
     */
    maxBoardingDelay: number;

    /**
     * Maximum number of minutes a driver can get early relative to the pickup time set in the reservation
     * @engine pickGapBefore
     */
    maxBoardingEarly: number;

    /**
     * Maximum number of minutes a driver can be late for the drop off time set in the reservation
     * @engine dropGapAfter
     */
    maxDropOffDelay: number;

    /**
     * Maximum number of minutes a driver can get early relative to the drop off time set in the reservation
     * @engine dropGapBefore
     */
    maxDropOffEarly: number;

    /**
     * Defines the maximum radius we try to look for trips to join a reservation to (When TripsJoinMode is set to either try or only join).
     * Radius is either in KM or Miles according to the given Geolocation.systemType defined in the request.
     */
    maxJoinRadius: number;

    /** Set a time limit (in minutes) for reservations try join to an existing trips (trips that are further away from this limit will be considered for try join) */
    maxJoinTimeRange: number;

    /**
     * Make all trips private.
     * @engine privateAll
     */
    onlyPrivate: boolean;

    /** Ensuring grouping of Leg A (outbound) and Leg B (return) for individual passengers */
    groupWaitAndReturn: WaitAndReturnGroupingModes;

    /**
     * Maximum number of pickup stops per trip
     * @engine maxPickStops
     */
    maxPickupStops: number;

    /**
     * Maximum number of drop-off stops per trip
     * @engine maxDropoffStops
     */
    maxDropoffStops: number;

    /**
     * Automatically snap booking origin or destination to the closest known location from our internal locations management module,
     * if it is within the snapToLocation radius. Value is in meters/foot based on your system type setting format.
     * @engine snapToLocation
     */
    snapToLocation: number;

    /**
     * This is the critical battery percentage that you do not want the system to go below during your journey.
     * For example, if set to 30%, the system will aim to keep your battery above this level.
     * @engine minimumBatteryThreshold
     */
    minimalBatteryThreshold: number;

    /**
     * Defines the maximum allowed distance (in kilometers or miles) between the first pickup and any subsequent pickups. Bookings with pickups outside this radius should not be combined.
     * @engine pickPhaseRadius
     */
    pickPhaseRadius: number;

    /**
     * Defines the maximum time (in minutes) allowed between the first pickup and any subsequent pickups. If the time difference exceeds this threshold, bookings should not be combined.
     * @engine pickPhaseMinutes
     */
    pickPhaseMinutes: number;

    /** Define the maximum number of bookings of a specific attribute that can occur on a trip simultaneously.
     * @engine maxSimultaneousBookings
     */
    maxSimultaneousBookings: number;

    /** When enabled, this feature reduces unnecessary detours by shifting pickup and drop-off points to optimized locations along the route. While this improves efficiency and travel time, it may require passengers to walk a short distance.
     * @engine snapToShapeEnabled
     */
    smoothRouteEnabled: boolean;

    /** Defines the maximum walking distance (in meters) that a passenger may be required to walk when “Smooth Route” is enabled. This setting only takes effect if “Smooth Route” is turned on.
     * @engine smoothRouteRadius
     */
    smoothRouteRadius: number;

    /** Defines whether Distance to cluster will be by Air Distance or Walking Distance.
     * @engine distanceType
     */
    distanceType: DistanceTypes;

    /**
     * When the parameter is set to default, we pass the flag hClusteringEnabled: false.
     * When the parameter is set to ClusteringEnabled, we pass hClusteringEnabled: true to the engine.
     * @engine hClusteringEnabled
     */
    routingMode: RoutingMode;

    /**
     * Time limit for the optimization to try and improve results, longer runs leave room to explore more possibilities.
     * @engine vrpRunningTime
     */
    vrpRunningTime: number;

    /**
     * Maximum number of passengers allowed in a generated cluster.
     */
    maxPassengersInCluster: number;

    /**
     * When enabled, VRP output clusters are kept instead of being split into individual reservations for rerouting.
     */
    keepVrpClusters: boolean;

    enableWillCallTime: boolean;

    defaultWillCallTime: number;

    /**
     * The system will try to group bookings with the same flight number into a single trip
     * Implemented using reservation group ID.
     */
    sameFlightNumber: boolean;
  };
  [SettingsCategories.EngineAssignSettings]: {
    /**
     * Should the engine change trip departure time slightly to improve driver assignment?
     * The engine will consider the routing rules of pick-up and drop-off max gaps if sets to true.
     * @engine doPushTrips
     */
    allowFlexibleTimes: boolean;

    /**
     * The higher the priority the higher the chances that someone that already assinged will be assigned again.
     * Give lower priority to balance the trips between active drivers and high prioirty to have the least amount of drivers necessary.
     * @engine ifTripDone
     */
    ifTripDone: number;

    /**
     * The higher the priority, the more each completed trip increases the driver's chances of being assigned again.
     * @engine perTripDone
     */
    perTripDone: number;

    /**
     * Give a high prioirty if you wish to balance the amount of drivers based on their salaries (Using the most drivers).
     * Give a low prioirty if you wish to use the least amount of drivers.
     * @engine perPriceDone
     */
    accumulatedSalaryScore: number;

    /**
     * Give higher priority if you wish to increase the chances that the driver that is the closest to the trip will be assigned.
     * @engine perMeterAway
     */
    distanceFromTripScore: number;

    /**
     * If you wish to keep drivers close to home (their starting location) you should give a high priority.
     * @engine perMeterAwayFromHome
     */
    distanceFromHomeScore: number;

    /**
     * Give higher priority if you wish to increase the chances that the driver that can get fastest to the starting point will be assigned.
     * @engine perSecondAway
     */
    timeFromTripScore: number;

    /**
     * A higher priority will increase the chances that a driver whos waiting for his next job the most time will be assigned.
     * @engine perSecondGap
     */
    gapTimeScore: number;

    /**
     * Period that the driver needs to stay idle to receive the ${idleBonusScore}
     * @engine idleBonusMinute
     */
    idleBonusMinutes: number;

    /**
     * The amount of points added to driver score after being idle for ${idleBonusMinute} minutes
     * @engine idleBonusScore
     */
    idleBonusScore: number;

    /**
     * The higher the priority, the more likely a driver that belongs to the trip's area will be assigned.
     * If you wish to fully restrict assignment for drivers from the same area as the trip, the give it the highest priorty.
     * @engine perAreaMatch
     */
    areaMatchScore: number;

    /**
     * This setting would have a switch to turn on/off (When off, keep the selected profiles in the background)
     * This setting will have a dropdown to select profiles to affect (multi-select) and will have the option “All” to choose
     */
    isAssignBlocked: boolean;

    /**
     * Which profiles should not be automatically assigned
     */
    assignBlockedProfiles: number[];

    /**
     * Attributes that should not be automatically assigned by the platform.
     */
    assignBlockedAttributes: number[];

    /**
     * The amount of KMPH set as the limit for the engine calculations.
     * This metric can save some time for the engine in case the driver will not be able to get to to the trip on time even if he's driving at that speed on a straight road (driver will be ignored for assignment in such case).
     * @engine airKMPH
     */
    speedLimit: number;

    /**
     * The maximum amount of trips a driver could be assigned to on a given request
     * @engine maxTrips
     */
    maxTripsPerDriver: number;

    /**
     * The maximum sum of salary a driver could be making in the process of assignment (and then other drivers will be prioritized)
     * @engine maxPrice
     */
    maxAccumulatedSalary: number;

    /**
     * The maximum length (in hours) of a driver shift. The engine wont let a driver be considered active more than this amount of hours.
     * @engine shiftMaxMinutes
     */
    maxShiftHours: number;

    /**
     * The default start location to be considered for a vehicle/driver if don't has its own
     * @engine defaultCarLocation
     */
    defaultCarLocation: AddressLocation;

    /**
     * Default number of seats to be considered for a vehicle if not defined
     * @engine defaultSeats
     */
    defaultCarSeats: number;

    /**
     * Default assignment mode (Actual Drivers vs Virtual Drivers)
     * @engine useVirtuals
     */
    assignmentType: TripAssignmentTypes;

    /**
     * Should assignment process add virtual drivers in a case when no appropriate driver found?
     * This parameter is only relevant in case that the assignment type is AssignmentTypes.ActualDrivers
     * @engine useVirtuals
     */
    addVirtualDrivers: boolean;

    /**
     * Whether or not shifts should be taken into consideration on assignment requests to the engine
     * @engine ignoreShifts
     */
    ignoreDriverShifts: boolean;

    /**
     * deprioritize vehicles with many empty seats.
     * @engine perEmptySeats
     */
    scorePerEmptySeats: number;

    /**
     * Maximum score awarded to the highest-priority attribute when assigning trips
     * @engine assignPriorityBonusMax
     */
    assignPriorityBonusMax: number;

    /**
     * Minimum score awarded to the lowest-priority attribute when assigning trips
     * @engine assignPriorityBonusMin
     */
    assignPriorityBonusMin: number;

    /**
     * Distance Form Next Trip
     * @engine perMeterAwayNext
     */
    nextTripDistanceScore: number;

    /**
     * how much time after a shift does the driver need to rest before starting a new shift.
     * @engine shiftGap
     */
    startShiftWithTrip: boolean;

    /**
     *
     *@engine startShiftWithTrip
     */
    shiftGap: number;

    /**
     * Whether drop-off time can fall outside driver availability during assignment.
     * @engine ignoreDropoffTime
     */
    ignoreDropoffTime: boolean;

    /**
     * Additional availability buffer in minutes applied after regular assignment.
     * @engine shiftBufferMinutes
     */
    shiftBuffer: number;

    /**
     *Drivers are prompted to take a rest after each trip.
     * @engine driverChill
     */
    driverRestAfterTrip: number;

    /**
     * @engine perCarPrice
     */
    scorePerVehicleCost: number;

    /**
     * @engine perAssignment
     */
    perAssignmentScore: number;

    /**
     * @engine perLoadedMeter
     */
    perLoadedDistance: number;

    /**
     * @engine algorithmCode
     */
    algorithmCode: string;

    /**
     * @engine driverAssignmentStrategy
     */
    driverAssignmentStrategy: DriverAssignmentStrategy;

    /**
     * Changing a driver to another vehicle with matching “Driver/Vehicle-Booking Attribute Matching”
     * attributes will not automatically unassign the Driver’s trip.
     */
    unassignOnVehicleChange: boolean;

    /**
     * The system will assign drivers from the same supplier to a passenger’s bookings within a single day, disregarding driver limitations.
     */
    consistentSupplier: boolean;

    /**
     * When true, assignment uses supplier ranking instead of driver priority
     */
    assignBySupplier: boolean;

    /**
     * When assignBySupplier is true, the driver assignment priorities will depend on the selected supplierPriorityMode.
     */
    supplierPriorityMode: SupplierPriorityModes;

    /**
     * Add shift-based trip allocation: drivers with longer shifts get higher scores, increasing their chance of receiving trips.
     * @engine perShiftHour
     */
    shiftAllocationWeight: number;
  };
  [SettingsCategories.EngineTrafficSettings]: {
    /**
     * This ratio will be used as a base to modify all duration estimation by some percentage to get more accureate results.
     * @engine base
     */
    baseDurationRatio: number;

    /**
     * This ratio will be reduced from the base ratio for every KM calculated (usefull on long trips)
     * @engine reducePerKm
     */
    ratioReducedPerKM: number;

    /**
     * Minmimum ratio to calculate duration with (in case base ratio was reduced for any reason)
     * @engine min
     */
    minDurationRatio: number;

    /**
     * An array represting 24 hours of the day and their respectives ratio (on top of the base ratio).
     * @engine hours
     */
    ratioByHours: IEngineTrafficHours;

    /**
     * Is the weekend starts at Sunday or Saturday? (used to calculate traffic on the weekend)
     * @engine weekend
     */
    weekendType: WeekendTypes;

    /**
     * Should all calculations be made on distance instead of duration?
     * @engine shortestDistance
     */
    routeByDistance: boolean;

    /**
     * Should the engine avoid Toll Roads (paid roads) when calculating trips?
     * @engine
     */
    avoidToll: boolean;
  };
  [SettingsCategories.PaymentSettings]: {
    paymentProvider: PaymentProviders;
  };
  [SettingsCategories.DriverScheduleSettings]: {
    /**
     * Allows drivers to create recurring shifts within a custom date range.
     */
    enableRecurringShifts: boolean;
    /**
     * Allows drivers to include break periods within their scheduled shifts.
     */
    allowBreaksInShifts: boolean;
    /**
     * Allows drivers to delete their scheduled shifts if necessary.
     */
    enableShiftDeletion: boolean;
    /**
     * Sets the minimum required time before a shift starts during which a driver is allowed to cancel it.
     */
    minimumShiftCancellationNotice: number;
    /**
     * Allows drivers to create new shifts in their schedule.
     */
    allowShiftCreation: boolean;
    /**
     * Defines the maximum duration of an availability time slot a driver can set for themselves.
     */
    maxShiftAvailability: number;
  };
  [SettingsCategories.Distribution]: {
    onTimePerformance: number;
    noShowRate: number;
    cancellationRate: number;
    acceptanceRate: number;
    responseTime: number;
    costEfficiency: number;
  };
};

// FIELDS
export type ISettingsMappingFieldBase = {
  name: string;
  description?: string;
  sort?: number;
  group?: string;
  isAdvanced?: boolean;
};

export type ISettingsMappingFieldTypes = {
  type: FieldsTypes.Number | FieldsTypes.Boolean | FieldsTypes.String | FieldsTypes.SearchAddress | FieldsTypes.Array;
};

export const SettingsExtraKeys = ['trafficRouteEnabled'];

export type SettingsExtraFields = {
  trafficRouteEnabled: boolean;
  editOnMap: boolean;
};

export type AllSettingsKeys =
  | keyof ISettingsFields[SettingsCategories.Geolocation]
  | keyof ISettingsFields[SettingsCategories.EngineRouteSettings]
  | keyof ISettingsFields[SettingsCategories.EngineAssignSettings]
  | keyof ISettingsFields[SettingsCategories.EngineTrafficSettings]
  | keyof SettingsExtraFields;

export type ISettingsMappingFieldCheckbox = {
  type: FieldsTypes.Checkbox;
  config: {
    options: { label: string; id: string }[];
  };
};

export type ISettingsMappingFieldSlider = {
  type: FieldsTypes.Slider;
  config: {
    max: number;
    min: number;
    step: number | null;
    minOff?: boolean;
    reverse?: boolean;
    startPoint?: number;
    constantSteps?: number[];
    minLabel?: string; // Customized label on the left of the slider (overrides 'off' label when min equals 0)
    /**
     * Explanation on how to use unlimited/infinity sliders:
     * To enable an unlimited/infinity slider, set `unlimited` to true. This will:
     *   - This will render an infinity icon on the right.
     *   - Any value higher than `max` is converted to `config.infinity` (if defined) or `999999`.
     *
     * We'll use the term "unlimited step" to describe the slider step that represents infinity.
     *  - If `config.step` is defined, the "unlimited step" is rendered as `config.max + config.step`.
     *  - If `step` is not defined, it means `config.constantSteps` must be provided:
     *     - If `constantSteps` includes a value higher than `max`, that highest value is used as the "unlimited step".
     *     - If no such value is present in `constantSteps`, no actual infinity value is applied.
     */
    unlimited?: boolean;
    infinity?: number;
  };
};

export type ISettingsMappingFieldSelect = {
  type: FieldsTypes.Select;
  config: {
    multi: boolean;
    collection?: keyof MServerSelectHttpServices;
    options?: MSelectOption[];
    selectAll?: boolean;
  };
};

export type ISettingsMappingFieldString = {
  type: FieldsTypes.String;
};

export type ISettingsMappingFieldArray = {
  type: FieldsTypes.Array;
  config: {
    max?: number;
    min?: number;
  };
};

export type SettingFieldBase = {
  label: string;
  description: string;
  controller: Controller;
  hide?: boolean;
  dependency?: {
    controller: AllSettingsKeys;
    comparison: DependencyOperatorTypes;
    value?: string | number | boolean | Array<string | number | boolean>;
  };
  isDisabled?: (data: ISettingsFields | null) => boolean;
  advancedSettingsPopup?: (
    field: ISettingField,
    data: ISettingsFields | null,
    onChange: (
      value: unknown,
      controller: Controller,
      popup?: (accept: () => void) => void,
      beforeChange?: (prevValue: unknown, newValue: unknown) => unknown,
    ) => void,
  ) => JSX.Element;
  popup?: (accept: () => void) => void;
  formatValue?: (value: unknown) => unknown;
  beforeChange?: (prevValue: unknown, newValue: unknown) => unknown;
};

export type ISettingField = SettingFieldBase &
  (
    | ISettingsMappingFieldTypes
    | ISettingsMappingFieldSelect
    | ISettingsMappingFieldSlider
    | ISettingsMappingFieldCheckbox
    | ISettingsMappingFieldArray
    | ISettingsMappingFieldString
  );

//CONTROLLERS
export type ControllerGeolocation = {
  category: SettingsCategories.Geolocation;
  setting: keyof ISettingsFields[SettingsCategories.Geolocation];
};

export type ControllerBookings = {
  category: SettingsCategories.Bookings;
  setting: keyof ISettingsFields[SettingsCategories.Bookings];
};

export type ControllerAuth = {
  category: SettingsCategories.Auth;
  setting: keyof ISettingsFields[SettingsCategories.Auth];
};

export type ControllerEngineRouteSettings = {
  category: SettingsCategories.EngineRouteSettings | SettingsCategories.EngineTrafficSettings;
  setting: keyof ISettingsFields[SettingsCategories.EngineRouteSettings];
};

export type ControllerEngineAssignSettings = {
  category: SettingsCategories.EngineAssignSettings;
  setting: keyof ISettingsFields[SettingsCategories.EngineAssignSettings];
};

export type ControllerDriverScheduleSettings = {
  category: SettingsCategories.DriverScheduleSettings;
  setting: keyof ISettingsFields[SettingsCategories.DriverScheduleSettings];
};

export type ControllerEngineTrafficSettings = {
  category: SettingsCategories.EngineTrafficSettings;
  setting: keyof ISettingsFields[SettingsCategories.EngineTrafficSettings] | keyof SettingsExtraFields;
};

export type ControllerDistributionSettings = {
  category: SettingsCategories.Distribution;
  setting: keyof ISettingsFields[SettingsCategories.Distribution] | keyof SettingsExtraFields;
};

export type Controller =
  | ControllerGeolocation
  | ControllerEngineRouteSettings
  | ControllerEngineAssignSettings
  | ControllerBookings
  | ControllerAuth
  | ControllerDriverScheduleSettings
  | ControllerEngineTrafficSettings
  | ControllerDistributionSettings;

export type ModifiedSetting = {
  controller: Controller;
  value: unknown;
};

export type ISettingsSubGroup = {
  title: string;
  subTitle?: string;
  isAdvanced?: boolean;
  isDisabled?: boolean;
  fields: ISettingField[];
  extraComponent?: React.FC<{ controllers: SettingFieldBase['controller'][]; data: ISettingsFields | null }>;
};

export type ISettingsGroup = {
  title: string;
  subTitle?: string;
  isAdvanced?: boolean;
  subGroups: ISettingsSubGroup[];
};

export enum SettingsTabIds {
  General = 'general',
  Routing = 'routing',
  DriverAssignment = 'Driver Assignment',
  Distribution = 'Distribution',
}

export type ISettingsTab = {
  id: SettingsTabIds;
  key: SettingsCategories;
  label: string;
  pageTitle: string;
  groups: ISettingsGroup[];
  hide?: boolean;
  hideTemplateDescriptions?: boolean;
};
